import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from "lucide-react";
import { useSelector } from "react-redux";
import styles from "../../_css/CollapsibleSection.module.css";

const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
  className = "",
  headerClassName = "",
  open, // new
  onHeaderClick, // new
}) => {
  const lang = useSelector((state) => state.language.lang);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const isOpen = open !== undefined ? open : internalOpen;
  
  // Convert children to array for navigation
  const childrenArray = React.Children.toArray(children);
  const totalItems = childrenArray.length;

  const handleHeaderClick = () => {
    if (onHeaderClick) {
      onHeaderClick();
    } else {
      setInternalOpen((o) => !o);
    }
  };

  const scrollToItem = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const items = container.children;
      
      if (items.length > index) {
        const targetItem = items[index];
        const containerRect = container.getBoundingClientRect();
        const itemRect = targetItem.getBoundingClientRect();
        
        // Calculate scroll position
        const scrollLeft = container.scrollLeft;
        const itemLeft = targetItem.offsetLeft;
        const itemWidth = targetItem.offsetWidth;
        const containerWidth = container.clientWidth;
        
        // Calculate the position to center the item
        const targetScrollLeft = itemLeft - (containerWidth - itemWidth) / 2;
        
        container.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollToItem(newIndex);
    }
  };

  const handleNext = () => {
    if (currentIndex < totalItems - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToItem(newIndex);
    }
  };

  // Reset index when section opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [open]);

  // Sync current index with scroll position
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    const handleScroll = () => {
      const items = container.children;
      if (items.length === 0) return;

      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;
      
      let closestIndex = 0;
      let minDistance = Infinity;

      for (let i = 0; i < items.length; i++) {
        const itemRect = items[i].getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(itemCenter - containerCenter);
        
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }

      setCurrentIndex(closestIndex);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  return (
    <div style={{ marginBottom: "12px" }} className={className}>
      <div
        className={headerClassName}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          fontWeight: 600,
          color: "#102554",
          fontSize: "17px",
          padding: "12px 24px",
        }}
        onClick={handleHeaderClick}
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp size={22} /> : <ChevronDown size={22} />}
      </div>
      <div
        className={
          isOpen
            ? `${styles.collapsibleContent} ${styles.collapsibleContentOpen}`
            : styles.collapsibleContent
        }
        style={{ marginTop: "0", display: !isOpen && 'none' }}
      >
        {isOpen && totalItems > 0 && (
          <div className={ `${styles.navigationContainer}`}>
            <button
              className={`${styles.navButton} ${ totalItems <= 3 && styles.navButtonDisabled || currentIndex === 0 ? styles.navButtonDisabled : ''}`}
              onClick={handlePrevious}
              disabled={ totalItems <= 3 || currentIndex === 0}
            >
              {lang === "ar" ? <ChevronRight size={20} color="#fff"/> : <ChevronLeft size={20} color="#fff"/>}
            </button>
            
            <div 
              ref={scrollContainerRef}
              className={styles.itemsContainer}
            >
              {childrenArray.map((child, index) => (
                <div key={index} className={styles.itemWrapper}>
                  {child}
                </div>
              ))}
            </div>
            
            <button
              className={`${styles.navButton} ${ totalItems <= 3 && styles.navButtonDisabled || currentIndex === totalItems - 1 ? styles.navButtonDisabled : ''}`}
              onClick={handleNext}
              disabled={ totalItems <= 3 || currentIndex === totalItems - 1}
            >
              {lang === "ar" ? <ChevronLeft size={18} color="#fff"/> : <ChevronRight size={18} color="#fff"/>}
            </button>
          </div>
        )}
        {!isOpen && children}
      </div>
    </div>
  );
};

export default CollapsibleSection; 